import React, { useState } from 'react';
import {
  CircularProgress,
  Chip,
  Button,
  Dialog
} from '@mui/material';
import {
  UserIcon,
  ShoppingBagIcon,
  DocumentTextIcon,
  TableCellsIcon,
  DocumentIcon
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import ConsolidatedInvoiceView from './ConsolidatedInvoiceView';
import { CURRENCY } from '../config/currency';

interface InvoiceOrderLineGroup {
  orderLineId: number | null;
  sessionCode: string;
  status: string;
  startedAt?: string;
  endedAt?: string;
  totalAmount?: number;
  paidAmount?: number;
  customerType?: string;
  invoicesTotal: number;
  customer?: {
    id?: number;
    username: string;
    email?: string;
    phone?: string;
    type: string;
  };
  table?: {
    id: number;
    name: string;
  };
  invoices: Invoice[];
  invoiceCount: number;
  consolidatedItems?: Array<{ id: number; name: string; quantity: number; price: number; }>;
  dateRange?: {
    from: string;
    to: string;
  };
}

interface Invoice {
  id: number;
  orderId: number;
  orderCustomOrderId: string;
  createdAt: string;
  total: number;
  status: string;
  customer?: {
    username: string;
    type: string;
  };
}

interface InvoiceOrderLineTreeProps {
  groups: any[];
  theme: any;
  loading?: boolean;
}

const InvoiceOrderLineTree: React.FC<InvoiceOrderLineTreeProps> = ({ groups, theme, loading = false }) => {
  const [selectedGroup, setSelectedGroup] = useState<any | null>(null);
  const [showConsolidatedView, setShowConsolidatedView] = useState(false);

  const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case 'ACTIVE': return theme.success || '#10b981';
      case 'CLOSED': return theme.gray?.[600] || '#6b7280';
      case 'CANCELLED': return theme.danger || '#ef4444';
      case 'COMPLETED': return theme.gray?.[600] || '#6b7280';
      case 'LEGACY': return theme.gray?.[500] || '#6b7280';
      default: return theme.gray?.[500] || '#6b7280';
    }
  };

  const getCustomerIcon = (type?: string) => {
    switch (type) {
      case 'registered':
        return <UserIcon className="h-4 w-4" />;
      case 'guest':
        return <UserIcon className="h-4 w-4" style={{ opacity: 0.6 }} />;
      case 'vendor':
      case 'steward':
        return <ShoppingBagIcon className="h-4 w-4" />;
      default:
        return <UserIcon className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <CircularProgress style={{ color: theme.primary }} />
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div className="text-center py-12">
        <DocumentTextIcon className="h-12 w-12 mx-auto mb-3" style={{ color: theme.gray?.[400] }} />
        <p style={{ color: theme.gray?.[600] }}>No invoices found</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {groups.map((group) => {
          const groupId = group.orderLineId || 'legacy';

          return (
            <div
              key={groupId}
              className="border rounded-xl overflow-hidden"
              style={{
                borderColor: theme.components?.card?.border,
                backgroundColor: theme.components?.card?.background
              }}
            >
              {/* OrderLine Header */}
              <div
                className="p-4"
                style={{
                  backgroundColor: theme.components?.card?.background
                }}
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      {getCustomerIcon(group.customer?.type)}
                      <div className="min-w-0 flex-1">
                        <p className="font-medium truncate" style={{ color: theme.text }}>
                          {group.customer?.username || 'Unknown Customer'}
                        </p>
                        <div className="flex items-center gap-2 text-sm flex-wrap" style={{ color: theme.gray?.[600] }}>
                          <span className="whitespace-nowrap">Session: {group.sessionCode}</span>
                          {group.table && (
                            <>
                              <span className="hidden sm:inline">•</span>
                              <div className="flex items-center gap-1 whitespace-nowrap">
                                <TableCellsIcon className="h-3 w-3" />
                                <span>{group.table.name}</span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-4">
                    <div className="text-left sm:text-right">
                      <p className="font-medium text-sm sm:text-base" style={{ color: theme.text }}>
                        {group.invoiceCount} {group.invoiceCount === 1 ? 'invoice' : 'invoices'}
                      </p>
                      <p className="text-xs sm:text-sm" style={{ color: theme.gray?.[600] }}>
                        Total: {CURRENCY.symbol}{group.invoicesTotal.toFixed(2)}
                      </p>
                    </div>
                    
                    <Chip
                      label={group.status}
                      size="small"
                      style={{
                        backgroundColor: getStatusColor(group.status) + '20',
                        color: getStatusColor(group.status),
                        fontWeight: 500,
                        fontSize: '0.75rem'
                      }}
                    />
                  </div>
                </div>
                
                {/* Invoice Button - Only for non-legacy groups */}
                {group.orderLineId !== null && (
                  <div className="mt-3">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<DocumentIcon className="h-4 w-4" />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedGroup(group);
                        setShowConsolidatedView(true);
                      }}
                      sx={{
                        backgroundColor: 'transparent !important',
                        color: (theme.gray?.[700] || '#374151') + ' !important',
                        border: `1px solid ${theme.gray?.[300] || '#d1d5db'} !important`,
                        textTransform: 'none',
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        px: { xs: 2, sm: 3 },
                        py: { xs: 0.5, sm: 0.75 },
                        minHeight: { xs: '32px', sm: '36px' },
                        boxShadow: 'none !important',
                        '&:hover': {
                          backgroundColor: (theme.gray?.[100] || '#f3f4f6') + ' !important',
                          borderColor: (theme.gray?.[400] || '#9ca3af') + ' !important'
                        }
                      }}
                    >
                      <span className="hidden xs:inline">View Invoice</span>
                      <span className="xs:hidden">View Invoice</span>
                    </Button>
                  </div>
                )}
                
                {group.startedAt && (
                  <div className="mt-2 flex items-center gap-4 text-sm" style={{ color: theme.gray?.[600] }}>
                    <span>Started: {format(new Date(group.startedAt), 'MMM d, h:mm a')}</span>
                    {group.endedAt && (
                      <span>Ended: {format(new Date(group.endedAt), 'h:mm a')}</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Consolidated Invoice Dialog */}
      <Dialog
        open={showConsolidatedView}
        onClose={() => setShowConsolidatedView(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: theme.background,
            maxHeight: '90vh'
          }
        }}
      >
        {selectedGroup && (
          <ConsolidatedInvoiceView
            group={selectedGroup}
            theme={theme}
            onClose={() => setShowConsolidatedView(false)}
          />
        )}
      </Dialog>
    </>
  );
};

export default InvoiceOrderLineTree;